"use client";

import { useSyncExternalStore } from "react";
import { useFeatureFlagVariantKey } from "posthog-js/react";
import type { ExperimentFlag } from "@/lib/experiments";
import { parseAbOverrides } from "@/lib/experimentOverride";

// The `?ab=` override is fixed for a page load (it only changes via navigation),
// so there is nothing to subscribe to — an empty subscribe is correct.
const noopSubscribe = () => () => {};

function useUrlOverride(flag?: ExperimentFlag): string | undefined {
  return useSyncExternalStore(
    noopSubscribe,
    () => (flag ? parseAbOverrides(window.location.search)[flag] : undefined),
    () => undefined, // server render has no URL override -> control
  );
}

// Resolved variant for an experiment. A `?ab=flag:variant` URL override (used by
// the admin preview) wins; otherwise the PostHog-assigned variant is used.
// Returns undefined on the server / until flags resolve, so callers server-render
// the control copy (SEO/LCP safe) and swap on hydration — matching the rest of
// the experiment layer.
export function useExperimentVariant(flag?: ExperimentFlag): string | undefined {
  const phVariant = useFeatureFlagVariantKey(flag ?? "__none__");
  const override = useUrlOverride(flag);

  if (override) return override;
  return typeof phVariant === "string" ? phVariant : undefined;
}
